"use client"

import * as React from 'react'
import Link from 'next/link'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import { Button } from '@open-mercato/ui/primitives/button'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import type { FilterDef, FilterValues } from '@open-mercato/ui/backend/FilterBar'

type HarmonogramRow = {
  id: string
  stationId: string
  slotType: string
  trackNumber: number
  platform: string | null
  arrivalTime: string
  departureTime: string
  daysOfWeek: number[]
  trainNumber: string | null
  validFrom: string
  validUntil: string | null
  isActive: boolean
}

type HarmonogramResponse = {
  items: Array<Record<string, unknown>>
  total: number
  page: number
  totalPages: number
}

const DAY_NAMES: Record<number, string> = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' }

function mapRow(item: Record<string, unknown>): HarmonogramRow | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    stationId: typeof item.station_id === 'string' ? item.station_id : '',
    slotType: typeof item.slot_type === 'string' ? item.slot_type : '',
    trackNumber: typeof item.track_number === 'number' ? item.track_number : 0,
    platform: typeof item.platform === 'string' ? item.platform : null,
    arrivalTime: typeof item.arrival_time === 'string' ? item.arrival_time : '',
    departureTime: typeof item.departure_time === 'string' ? item.departure_time : '',
    daysOfWeek: Array.isArray(item.days_of_week) ? (item.days_of_week as number[]) : [],
    trainNumber: typeof item.train_number === 'string' ? item.train_number : null,
    validFrom: typeof item.valid_from === 'string' ? item.valid_from : '',
    validUntil: typeof item.valid_until === 'string' ? item.valid_until : null,
    isActive: typeof item.is_active === 'boolean' ? item.is_active : true,
  }
}

export default function HarmonogramPage() {
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: 'terminal-scheduling-harmonogram-list',
  })
  const [rows, setRows] = React.useState<HarmonogramRow[]>([])
  const [page, setPage] = React.useState(1)
  const pageSize = 50
  const [total, setTotal] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})
  const [isLoading, setIsLoading] = React.useState(true)
  const [reloadToken, setReloadToken] = React.useState(0)
  const scopeVersion = useOrganizationScopeVersion()
  const t = useT()

  const filters = React.useMemo<FilterDef[]>(() => [
    {
      id: 'isActive',
      label: t('terminal_scheduling.harmonogram.filters.isActive', 'Active'),
      type: 'select' as const,
      options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
    },
  ], [t])

  const queryParams = React.useMemo(() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('pageSize', String(pageSize))
    if (search.trim()) params.set('search', search.trim())
    const isActive = filterValues.isActive
    if (typeof isActive === 'string' && isActive.trim()) params.set('isActive', isActive)
    return params.toString()
  }, [filterValues, page, search])

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      try {
        const fallback: HarmonogramResponse = { items: [], total: 0, page: 1, totalPages: 1 }
        const call = await apiCall<HarmonogramResponse>(
          `/api/terminal_scheduling/harmonogram?${queryParams}`,
          undefined,
          { fallback },
        )
        if (!call.ok) {
          flash(t('terminal_scheduling.harmonogram.error.load', 'Failed to load harmonogram'), 'error')
          return
        }
        const payload = call.result ?? fallback
        if (!cancelled) {
          const items = Array.isArray(payload.items) ? payload.items : []
          setRows(items.map(mapRow).filter((row): row is HarmonogramRow => row !== null))
          setTotal(typeof payload.total === 'number' ? payload.total : items.length)
          setTotalPages(typeof payload.totalPages === 'number' ? payload.totalPages : 1)
        }
      } catch {
        if (!cancelled) flash(t('terminal_scheduling.harmonogram.error.load', 'Failed to load harmonogram'), 'error')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [queryParams, reloadToken, scopeVersion, t])

  const handleDelete = React.useCallback(async (entry: HarmonogramRow) => {
    const confirmed = await confirm({
      title: t('terminal_scheduling.harmonogram.deleteConfirm', 'Delete harmonogram entry?'),
      variant: 'destructive',
    })
    if (!confirmed) return
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          `/api/terminal_scheduling/harmonogram?id=${encodeURIComponent(entry.id)}`,
          { method: 'DELETE', headers: { 'content-type': 'application/json' } },
          { errorMessage: t('terminal_scheduling.harmonogram.error.delete', 'Failed to delete entry') },
        ),
        context: {},
      })
      setRows((prev) => prev.filter((r) => r.id !== entry.id))
      setTotal((prev) => Math.max(prev - 1, 0))
      flash(t('terminal_scheduling.harmonogram.flash.deleted', 'Harmonogram entry deleted'), 'success')
    } catch (error) {
      const message = error instanceof Error ? error.message : t('terminal_scheduling.harmonogram.error.delete', 'Failed to delete entry')
      flash(message, 'error')
    }
  }, [confirm, runMutation, t])

  const columns = React.useMemo<ColumnDef<HarmonogramRow>[]>(() => {
    const noValue = <span className="text-muted-foreground text-sm">-</span>
    return [
      {
        accessorKey: 'trainNumber',
        header: t('terminal_scheduling.harmonogram.columns.trainNumber', 'Train No.'),
        cell: ({ row }) => row.original.trainNumber ? <span className="font-mono font-medium">{row.original.trainNumber}</span> : noValue,
      },
      {
        accessorKey: 'slotType',
        header: t('terminal_scheduling.harmonogram.columns.slotType', 'Type'),
        cell: ({ row }) => row.original.slotType,
      },
      {
        accessorKey: 'trackNumber',
        header: t('terminal_scheduling.harmonogram.columns.trackNumber', 'Track'),
        cell: ({ row }) => <span className="font-mono">{row.original.trackNumber}</span>,
      },
      {
        accessorKey: 'arrivalTime',
        header: t('terminal_scheduling.harmonogram.columns.arrivalTime', 'Arrival'),
        cell: ({ row }) => row.original.arrivalTime,
      },
      {
        accessorKey: 'departureTime',
        header: t('terminal_scheduling.harmonogram.columns.departureTime', 'Departure'),
        cell: ({ row }) => row.original.departureTime,
      },
      {
        accessorKey: 'daysOfWeek',
        header: t('terminal_scheduling.harmonogram.columns.daysOfWeek', 'Days'),
        cell: ({ row }) => row.original.daysOfWeek.map((d) => DAY_NAMES[d] ?? d).join(', '),
      },
      {
        accessorKey: 'isActive',
        header: t('terminal_scheduling.harmonogram.columns.isActive', 'Active'),
        cell: ({ row }) => row.original.isActive ? <span className="text-green-600">Yes</span> : <span className="text-red-600">No</span>,
      },
    ]
  }, [t])

  return (
    <Page>
      <PageBody>
        <DataTable<HarmonogramRow>
          title={t('terminal_scheduling.harmonogram.title', 'Harmonogram')}
          refreshButton={{
            label: t('terminal_scheduling.harmonogram.actions.refresh', 'Refresh'),
            onRefresh: () => { setSearch(''); setPage(1); setReloadToken((n) => n + 1) },
          }}
          actions={(
            <Button asChild>
              <Link href="/backend/terminal-scheduling/harmonogram/create">
                {t('terminal_scheduling.harmonogram.actions.create', 'New Entry')}
              </Link>
            </Button>
          )}
          columns={columns}
          data={rows}
          searchValue={search}
          onSearchChange={(value) => { setSearch(value); setPage(1) }}
          searchPlaceholder={t('terminal_scheduling.harmonogram.searchPlaceholder', 'Search by train number...')}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={(v) => { setFilterValues(v); setPage(1) }}
          onFiltersClear={() => { setFilterValues({}); setPage(1) }}
          perspective={{ tableId: 'terminal_scheduling.harmonogram.list' }}
          rowActions={(row) => (
            <RowActions
              items={[
                {
                  id: 'delete',
                  label: t('terminal_scheduling.harmonogram.actions.delete', 'Delete'),
                  destructive: true,
                  onSelect: () => { void handleDelete(row) },
                },
              ]}
            />
          )}
          pagination={{ page, pageSize, total, totalPages, onPageChange: setPage }}
          isLoading={isLoading}
        />
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
